import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Button from '../Button';
import { dollarsToCents, centsToDollars } from '../../lib/money';
import { useAuth } from '../../context/AuthContext';
import { useCatalog } from '../../context/CatalogContext';
import './CampPriceOptionsEditor.css';

const BLANK = {
  name: '', description: '', price_dollars: '', position_slugs: [],
  checkin_time: '', field_time: '',
  sales_start_at: '', sales_end_at: '', capacity: '', sort_order: 0,
  is_active: true,
};

// A `datetime-local` input carries no timezone info at all — it is just a
// wall-clock string. Unlike camp.date/end_date (naive wall-clock stored
// verbatim in the camp's own IANA zone, see CampsPage.js), the backend
// stores camp_price_options.sales_start_at/sales_end_at as UTC. A sales
// window is an admin-side "is this on sale right now" instant, not a
// rendered venue-local time like the event's start, so there is no need to
// involve the camp's timezone here at all: we treat what the admin types as
// their own browser-local time and convert to/from UTC with the native
// Date object, which already knows the browser's offset for that date
// (DST included) — no timezone library required.
const localToUtcIso = (v) => (v ? new Date(v).toISOString() : null);
const utcIsoToLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Repeatable price-option rows for one camp, mounted inside the camp
 * create/edit modal on CampsPage.
 *
 * Positions come from CatalogContext (GET /api/sports/catalog under the
 * hood) for this camp's sport — never a hardcoded list. A camp with no
 * sport set (sportSlug is null/undefined) simply gets no position picker,
 * which still leaves flat-price and date-window options fully usable.
 *
 * Most camps will never add a row here at all: with zero options the camp
 * charges its own base price, which is the majority case, not a
 * degraded one — the empty state below says so instead of implying
 * options are required.
 */
/**
 * Two modes.
 *
 *  - campId set   — the camp exists; rows are fetched and each Save/Delete hits
 *                   the API immediately. Unchanged behaviour.
 *  - campId null  — the camp is being CREATED and has no id yet, so rows are
 *                   held in the parent's state via value/onChange and the parent
 *                   POSTs them once the camp exists. Without this the editor was
 *                   a dead end reading "Save the camp first", which is exactly
 *                   what an admin hits setting up a camp with per-position
 *                   pricing.
 */
export default function CampPriceOptionsEditor({ campId, sportSlug, value, onChange }) {
  const isBuffered = !campId;
  const { API_URL } = useAuth();
  const { groupedPositionsFor } = useCatalog();
  const [fetchedRows, setFetchedRows] = useState([]);
  const rows = isBuffered ? (value || []) : fetchedRows;
  const setRows = isBuffered ? (next) => onChange(
    typeof next === 'function' ? next(value || []) : next) : setFetchedRows;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const getAxiosConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });

  const load = useCallback(() => {
    if (!campId) return;   // buffered mode has nothing to fetch
    axios.get(`${API_URL}/camps/${campId}/price-options`, getAxiosConfig())
      .then((r) => setRows((r.data.price_options || []).map((o) => ({
        ...o,
        price_dollars: centsToDollars(o.price_cents),
        sales_start_at: utcIsoToLocalInput(o.sales_start_at),
        sales_end_at: utcIsoToLocalInput(o.sales_end_at),
      }))))
      .catch(() => setError('Could not load price options.'));
  }, [campId, API_URL]);

  useEffect(() => { load(); }, [load]);

  const positionGroups = sportSlug ? groupedPositionsFor(sportSlug) : [];
  const hasPositions = positionGroups.some((g) => g.positions.length > 0);

  const save = async (row) => {
    // A `|| 0` fallback here would quietly create a live $0.00 option from an
    // empty or malformed field, which is worse than refusing to save.
    const priceCents = dollarsToCents(row.price_dollars);
    if (priceCents === null) {
      setError(`Enter a price in dollars for "${row.name || 'this option'}", e.g. 135 or 135.00`);
      return;
    }
    if (isBuffered) {
      // Nothing to POST yet — the row already lives in the parent's state and
      // is sent when the camp is created.
      setError('');
      return;
    }
    setBusy(true); setError('');
    const body = {
      name: row.name,
      description: row.description || null,
      price_cents: priceCents,
      position_slugs: row.position_slugs?.length ? row.position_slugs : null,
      sales_start_at: localToUtcIso(row.sales_start_at),
      sales_end_at: localToUtcIso(row.sales_end_at),
      capacity: row.capacity === '' || row.capacity === null
        ? null : parseInt(row.capacity, 10),
      sort_order: parseInt(row.sort_order, 10) || 0,
      is_active: !!row.is_active,
      checkin_time: row.checkin_time || null,
      field_time: row.field_time || null,
    };
    try {
      if (row.id) {
        await axios.put(`${API_URL}/camps/${campId}/price-options/${row.id}`, body, getAxiosConfig());
      } else {
        await axios.post(`${API_URL}/camps/${campId}/price-options`, body, getAxiosConfig());
      }
      load();
    } catch {
      setError('Could not save that option.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (isBuffered || !row.id) { setRows(rows.filter((r) => r !== row)); return; }
    if (!window.confirm(`Delete "${row.name}"? Registrations that bought it are kept.`)) return;
    try {
      await axios.delete(`${API_URL}/camps/${campId}/price-options/${row.id}`, getAxiosConfig());
      load();
    } catch {
      setError('Could not delete that option.');
    }
  };

  const patch = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));


  return (
    <div className="cpo">
      {error && <div className="form-field__error" role="alert">{error}</div>}
      {rows.map((row, i) => (
        <div key={row.id || `new-${i}`} className="cpo-option">
          <div className="cpo-grid cpo-grid--main">
            <div className="form-field">
              <label className="form-field__label" htmlFor={`pco-name-${i}`}>Option name</label>
              <input
                id={`pco-name-${i}`}
                className="form-field__input" value={row.name}
                onChange={(e) => patch(i, 'name', e.target.value)}
                placeholder="Early bird"
              />
            </div>
            <div className="form-field">
              <label className="form-field__label" htmlFor={`pco-price-${i}`}>Price ($)</label>
              <input
                id={`pco-price-${i}`}
                type="text" inputMode="decimal" className="form-field__input"
                placeholder="135.00"
                value={row.price_dollars}
                onChange={(e) => patch(i, 'price_dollars', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label className="form-field__label" htmlFor={`pco-capacity-${i}`}>Capacity</label>
              <input
                id={`pco-capacity-${i}`}
                type="number" min="0" className="form-field__input"
                value={row.capacity ?? ''} placeholder="unlimited"
                onChange={(e) => patch(i, 'capacity', e.target.value)}
              />
            </div>
          </div>
          <div className="cpo-grid cpo-grid--times">
            <div className="form-field">
              <label className="form-field__label" htmlFor={`pco-checkin-${i}`}>Check-in</label>
              <input
                id={`pco-checkin-${i}`}
                type="text" className="form-field__input" placeholder="9:00 AM"
                value={row.checkin_time || ''}
                onChange={(e) => patch(i, 'checkin_time', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label className="form-field__label" htmlFor={`pco-field-${i}`}>On field</label>
              <input
                id={`pco-field-${i}`}
                type="text" className="form-field__input" placeholder="10:00 AM"
                value={row.field_time || ''}
                onChange={(e) => patch(i, 'field_time', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label className="form-field__label" htmlFor={`pco-start-${i}`}>On sale from</label>
              <input
                id={`pco-start-${i}`}
                type="datetime-local" className="form-field__input"
                value={row.sales_start_at || ''}
                onChange={(e) => patch(i, 'sales_start_at', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label className="form-field__label" htmlFor={`pco-end-${i}`}>On sale until</label>
              <input
                id={`pco-end-${i}`}
                type="datetime-local" className="form-field__input"
                value={row.sales_end_at || ''}
                onChange={(e) => patch(i, 'sales_end_at', e.target.value)}
              />
            </div>
          </div>
          {hasPositions && (
            <div className="form-field cpo-positions">
              <label className="form-field__label" htmlFor={`pco-positions-${i}`}>Positions</label>
              <select
                id={`pco-positions-${i}`}
                multiple className="form-field__input" value={row.position_slugs || []}
                onChange={(e) => patch(i, 'position_slugs',
                  Array.from(e.target.selectedOptions, (o) => o.value))}
              >
                {positionGroups.map((g) => (
                  g.group ? (
                    <optgroup key={g.group} label={g.group}>
                      {g.positions.map((p) => (
                        <option key={p.slug} value={p.slug}>{p.label}</option>
                      ))}
                    </optgroup>
                  ) : g.positions.map((p) => (
                    <option key={p.slug} value={p.slug}>{p.label}</option>
                  ))
                ))}
              </select>
              <span className="cpo-hint">
                Leave none selected to offer this option to every position.
              </span>
            </div>
          )}
          <div className="cpo-actions">
            <label className="cpo-active">
              <input
                type="checkbox" checked={!!row.is_active}
                onChange={(e) => patch(i, 'is_active', e.target.checked)}
              />
              Active
            </label>
            <Button variant="primary" size="sm" disabled={busy || !row.name} onClick={() => save(row)}>
              Save
            </Button>
            <Button variant="secondary" size="sm" onClick={() => remove(row)}>
              Delete
            </Button>
          </div>
        </div>
      ))}
      <Button
        variant="secondary" size="sm"
        onClick={() => setRows([...rows, { ...BLANK, sort_order: rows.length }])}
      >
        + Add price option
      </Button>
      <p style={{ fontSize: 13, color: '#64748b', marginTop: 8 }}>
        With no options here, the camp charges its base price above. With one or
        more, the registrant chooses one and is charged that option&apos;s price.
      </p>
    </div>
  );
}
